import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  IconButton,
  Box,
  Paper,
  styled,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material'
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import { DocumentDrawer, SelectedDocument, TDocument } from '../DocumentDrawer'
import { useTranslation } from 'react-i18next'
import { getAllDocumentsNewFlow } from '@/store/knowledge/actions'
import { useDispatch } from 'react-redux'

interface ContenteditableProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  loading?: boolean
  viewMode?: boolean
}

// Custom styled contenteditable div
const EditableDiv = styled('div')({
  outline: 'none',
  minHeight: '100px',
  whiteSpace: 'pre-wrap',
  width: '100%',
  '& .linkedText': {
    color: '#001DE0',
    fontWeight: 500,
    cursor: 'pointer',
  },
  '& .linkedText:hover': {
    color: 'white',
    backgroundColor: '#1a34e3',
    borderRadius: '2px',
  },
  '& p': {
    fontSize: '14px',
  },
})

const linkRegex = /\[([^\]]+)\]\(document_id:\/\/([^)]+)\)/g

export const Contenteditable: React.FC<ContenteditableProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter text here...',
  className = '',
  style = {},
  loading = false,
  viewMode = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedText, setSelectedText] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<
    SelectedDocument[]
  >([])

  // TODO: start - state for managing documents
  const [documents, setDocuments] = useState<TDocument[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  // console.log("🚀 ~ documents:", documents)
  const [yearOptions, setYearOptions] = useState<string[]>([])
  const [moduleOptions, setModuleOptions] = useState<string[]>([])
  const [topicOptions, setTopicOptions] = useState<string[]>([])
  // TODO: end - state for managing documents

  const [savedRange, setSavedRange] = useState<Range | null>(null)
  const [hoverElement, setHoverElement] = useState<{
    element: HTMLElement | null
    position: { top: number; left: number } | null
  }>({ element: null, position: null })

  const [selectedRangeElement, setSelectedRangeElement] = useState<{
    element: HTMLElement | null
    position: { top: number; left: number } | null
  }>({ element: null, position: null })

  const editableDivRef = useRef<HTMLDivElement>(null)

  const fetchDocuments = async () => {
    // setLoading(true)
    try {
      const { documents: documentsRes }: any = await dispatch(
        getAllDocumentsNewFlow({
          page: 1,
          page_size: 10000,
        }),
      )
      if (documentsRes) {
        setDocuments(documentsRes)
        // TODO: pass as props to DocumentDrawer
        // setFilteredDocuments(documentsRes)

        const uniqueYears = new Set(
          documentsRes.map((doc: TDocument) => doc.knowledge_group_1_name),
        )
        setYearOptions(Array.from(uniqueYears).filter(Boolean) as string[])

        const uniqueModules = new Set(
          documentsRes.map((doc: TDocument) => doc.knowledge_group_2_name),
        )
        setModuleOptions(Array.from(uniqueModules).filter(Boolean) as string[])

        const uniqueTopics = new Set(
          documentsRes.map((doc: TDocument) => doc?.knowledge_group_3_name),
        )

        setTopicOptions(Array.from(uniqueTopics).filter(Boolean) as string[])
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
    } finally {
      setIsLoaded(true)
      // setLoading(false)
    }
  }

  useEffect(() => {
    fetchDocuments()
  }, [])

  const handleInput = () => {
    if (editableDivRef.current) {
      const html = editableDivRef.current.innerHTML

      const updatedRawContent = convertHtmlToFormat(html)

      if (onChange) {
        onChange(updatedRawContent)
      }
    }
  }

  const convertHtmlToFormat = (html: string) => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    // Find all linked text spans and replace them with the markdown format
    const linkedSpans = tempDiv.querySelectorAll('.linkedText')
    linkedSpans.forEach(span => {
      const documentId = span.getAttribute('data-document-id')
      const innerText = span.textContent || ''

      // Create a text node with the markdown format
      const formattedText = document.createTextNode(
        `[${innerText}](${documentId})`,
      )

      // Replace the span with the formatted text
      span.parentNode?.replaceChild(formattedText, span)
    })

    // Convert the HTML to plain text while preserving line breaks
    let content = ''
    const childNodes = Array.from(tempDiv.childNodes)

    childNodes.forEach((node, index) => {
      // For paragraphs and divs, add newlines between them
      if (node.nodeName === 'P' || node.nodeName === 'DIV') {
        content += node.textContent
        // Add newline after paragraphs except for the last one
        if (index < childNodes.length - 1) {
          content += '\n'
        }
      }
      // Handle BR tags
      else if (node.nodeName === 'BR') {
        content += '\n'
      }
      // Handle text nodes and other elements
      else {
        content += node.textContent
      }
    })

    return content
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault() // Prevent default behavior

      // Insert a <br> element at the cursor position
      const selection = window.getSelection()
      const range = selection?.getRangeAt(0)

      if (range && editableDivRef.current) {
        // Create a <br> element
        const br = document.createElement('br')

        // Insert the <br> element at the cursor position
        range.deleteContents()
        range.insertNode(br)

        // Move caret after the <br>
        range.setStartAfter(br)
        range.setEndAfter(br)
        selection?.removeAllRanges()
        selection?.addRange(range)

        // Update content via onChange
        const html = editableDivRef.current.innerHTML
        const updatedRawContent = convertHtmlToFormat(html)

        if (onChange) {
          onChange(updatedRawContent)
        }
      }
    }
    if (['i', 'b', 'u'].includes(event.key) && (event.ctrlKey || event.metaKey))
      event.preventDefault()
  }

  const handleLinkedTextHover = (event: MouseEvent) => {
    const span = event.currentTarget as HTMLElement
    if (span) {
      setHoverElement({
        element: span,
        position: {
          top: span.offsetTop, // Position above the text
          left: span.offsetLeft, // Position at the right end of the text
        },
      })
    }
  }

  const tooltipRef = useRef<HTMLDivElement>(null)

  const handleLinkedTextLeave = (event: MouseEvent) => {
    // Don't hide if moving toward tooltip
    const relatedTarget = event.relatedTarget as HTMLElement
    if (tooltipRef.current?.contains(relatedTarget)) {
      return // Don't hide if moving to tooltip
    }

    // Small delay to allow clicking on the floating toolbar
    setTimeout(() => {
      // Check if mouse is over the tooltip now
      if (tooltipRef.current?.matches(':hover')) {
        return
      }
      setHoverElement({ element: null, position: null })
    }, 200)
  }

  const handleRemoveLink = (element: HTMLElement) => {
    if (!editableDivRef.current || !element) return

    // Get the text content from the span
    const textContent = element.textContent || ''

    // Create a text node to replace the span
    const textNode = document.createTextNode(textContent)

    // Replace the span with the text node
    element.parentNode?.replaceChild(textNode, element)

    // Update the content
    if (onChange && editableDivRef.current) {
      const html = editableDivRef.current.innerHTML
      const formatted = convertHtmlToFormat(html)
      onChange(formatted)
    }

    // Reset hover state
    setHoverElement({ element: null, position: null })
  }

  const handleEditLink = useCallback((element: HTMLElement) => {
    if (!element) return

    // Get the text content from the span
    const textContent = element.textContent || ''

    // Set the selected text and save a range for the span
    setSelectedText(textContent)

    // Create a range for the span
    const range = document.createRange()
    range.selectNode(element)
    setSavedRange(range)

    // Open the document drawer
    const documentIds = element.getAttribute('data-document-id')
    if (documentIds) {
      const documents = documentIds
        .split(';')
        .map(item => item.replace('document_id://', ''))
        .map(url => {
          const [id, query] = url.split('?')
          const params = new URLSearchParams(query)
          const fromPage = params.get('pageFrom')
          const toPage = params.get('pageTo')

          return {
            id,
            name: '',
            allPages: !fromPage && !toPage,
            fromPage: Number(fromPage) || 0,
            toPage: Number(toPage) || 0,
          }
        })
      setSelectedDocumentIds(documents)
    }

    setDrawerOpen(true)

    // Reset hover state
    setHoverElement({ element: null, position: null })
  }, [])

  const handleLinkedTextClick = useCallback(
    (event: Event) => {
      handleEditLink(event.currentTarget as HTMLElement)
    },
    [handleEditLink],
  )

  const attachLinkListeners = useCallback(() => {
    // First remove any existing listeners to prevent duplicates
    const existingLinks = document.querySelectorAll('.linkedText')
    existingLinks.forEach(span => {
      span.removeEventListener('click', handleLinkedTextClick)
      span.removeEventListener(
        'mouseenter',
        handleLinkedTextHover as EventListener,
      )
      span.removeEventListener(
        'mouseleave',
        handleLinkedTextLeave as EventListener,
      )
    })

    // Then add listeners to all links
    if (editableDivRef.current) {
      const linkedText = editableDivRef.current.querySelectorAll('.linkedText')
      linkedText.forEach(span => {
        span.addEventListener('click', handleLinkedTextClick)
        span.addEventListener(
          'mouseenter',
          handleLinkedTextHover as EventListener,
        )
        span.addEventListener(
          'mouseleave',
          handleLinkedTextLeave as EventListener,
        )
      })
    }
  }, [handleLinkedTextClick])

  const handleIconClick = () => {
    const selection = window.getSelection()
    const range = selection.getRangeAt(0)

    setSavedRange(range)
    const text = range.toString()

    setSelectedText(text)
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedDocumentIds([])
    setSavedRange(null)
  }

  const handleDocumentSelect = (documents: SelectedDocument[]) => {
    if (!editableDivRef.current || !savedRange) {
      return
    }

    const documentLinks = documents
      .map(
        document =>
          `document_id://${document.id}${
            document.allPages
              ? ''
              : `?pageFrom=${document.fromPage}&pageTo=${document.toPage}`
          }`,
      )
      .join(';')

    const span = document.createElement('span')
    span.className = 'linkedText'
    span.setAttribute('data-document-id', documentLinks)

    const documentName =
      documents.length > 1
        ? `${documents[0].name} +${documents.length - 1}`
        : documents[0].name
    span.textContent = selectedText || documentName

    // Restore the saved range
    const selection = window.getSelection()
    if (selection) {
      selection.removeAllRanges()
      selection.addRange(savedRange) // Restore the saved range
    }

    // Replace the selected text with the span
    savedRange.deleteContents()
    savedRange.insertNode(span)

    // Move the cursor after the inserted span
    selection?.collapse(span, 1) // Collapse the selection to the end of the inserted span

    // Update the content in onChange
    if (onChange) {
      const html = editableDivRef.current.innerHTML
      const formatted = convertHtmlToFormat(html)
      onChange(formatted)
    }

    attachLinkListeners()
    setSavedRange(null) // Clear the saved range
  }

  const convertFormatToHtml = useCallback(
    (text: string) => {
      const documentIds = documents.map(document => document._id)

      // const brPlaceholder = '___BR_PLACEHOLDER___'
      // const processedText = text.replace(/<br\s*\/?>/gi, brPlaceholder)

      const escapedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      // escapedText = escapedText.replace(new RegExp(brPlaceholder, 'g'), '<br>')

      return escapedText.replace(linkRegex, (_match, linkText, documentId) => {
        const documentIdValid = documentId
          .split(';')
          .filter((id: string) => {
            const idOnly = id.replace('document_id://', '').split('?')[0]

            return documentIds.includes(idOnly)
          })
          .join(';')

        return documentIdValid.length
          ? `<span class="linkedText" data-document-id="document_id://${documentId}">${linkText}</span>`
          : linkText
      })
    },
    [documents],
  )

  useEffect(() => {
    // console.log('🚀 ~ useEffect ~ documents:', documents)
    if (
      isLoaded &&
      editableDivRef.current &&
      value !== convertHtmlToFormat(editableDivRef.current.innerHTML)
    ) {
      editableDivRef.current.innerHTML = convertFormatToHtml(value)
      attachLinkListeners()
    }
  }, [value, isLoaded, convertFormatToHtml])

  useEffect(() => {
    // Set up a MutationObserver to watch for changes to the DOM
    const observer = new MutationObserver(() => {
      attachLinkListeners()
    })

    if (editableDivRef.current) {
      observer.observe(editableDivRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      })

      // Initial setup
      attachLinkListeners()
    }

    return () => {
      observer.disconnect()
      // Clean up existing listeners
      const linkedText = document.querySelectorAll('.linkedText')
      linkedText.forEach(span => {
        span.removeEventListener('click', handleLinkedTextClick)
        span.removeEventListener(
          'mouseenter',
          handleLinkedTextHover as EventListener,
        )
        span.removeEventListener(
          'mouseleave',
          handleLinkedTextLeave as EventListener,
        )
      })
    }
  }, [])

  const handleTooltipLeave = () => {
    // Hide tooltip when mouse leaves it
    setTimeout(() => {
      setHoverElement({ element: null, position: null })
    }, 200)
  }

  const handleTooltipSelectionRangeLeave = () => {
    // Hide tooltip when mouse leaves it
    setTimeout(() => {
      setSelectedRangeElement({ element: null, position: null })
    }, 200)
  }

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0)
      const text = range.toString().trim()

      if (text && editableDivRef.current?.contains(selection.anchorNode)) {
        const rect = range.getBoundingClientRect()
        const editableDivRect = editableDivRef.current.getBoundingClientRect()

        setSelectedRangeElement({
          element: range.startContainer.parentElement,
          position: {
            top: rect.top - editableDivRect.top + 16,
            left: rect.left - editableDivRect.left + 16,
          },
        })
      } else {
        setSelectedRangeElement({ element: null, position: null })
      }
    } else {
      setSelectedRangeElement({ element: null, position: null })
    }
  }, [])

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange)
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [handleSelectionChange])

  if (viewMode)
    return (
      <EditableDiv
        ref={editableDivRef}
        contentEditable
        onInput={handleInput}
        suppressContentEditableWarning
        className={className}
        style={style}
        data-placeholder={placeholder}
        onBlur={e => {
          if (['<p><br></p>', '<br>'].includes(e.currentTarget.innerHTML)) {
            e.currentTarget.innerHTML = ''
          }
        }}
        sx={{
          '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: '#aaa',
            pointerEvents: 'none',
            display: 'block',
          },
        }}
      />
    )

  return (
    <Box
      sx={{ position: 'relative', border: '1px solid #ddd', borderRadius: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1,
          mb: 1,
          mx: '12px',
          borderBottom: '1px solid #BEC3D1',
        }}
      >
        <Button
          sx={{ color: '#676267' }}
          onClick={handleIconClick}
          startIcon={<DatasetLinkedIcon />}
          color="inherit"
        >
          {t('Link Reference')}
        </Button>
      </Box>

      <Paper
        elevation={1}
        sx={{
          position: 'relative',
          minHeight: '150px',
          p: 2,
        }}
      >
        {loading ? (
          <Box>
            <CircularProgress size="20px" color="inherit" sx={{ mr: 2 }} />
            Generating...
          </Box>
        ) : (
          <EditableDiv
            ref={editableDivRef}
            onPaste={e => {
              e.preventDefault()
              var text = e.clipboardData.getData('text/plain')

              document.execCommand('insertText', false, text)
            }}
            contentEditable
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            suppressContentEditableWarning
            className={className}
            style={style}
            data-placeholder={placeholder}
            onBlur={e => {
              if (['<p><br></p>', '<br>'].includes(e.currentTarget.innerHTML)) {
                e.currentTarget.innerHTML = ''
              }
            }}
            sx={{
              '&:empty:before': {
                content: 'attr(data-placeholder)',
                color: '#aaa',
                pointerEvents: 'none',
                display: 'block',
              },
            }}
          />
        )}
      </Paper>

      {hoverElement.element && hoverElement.position && (
        <Paper
          ref={tooltipRef}
          elevation={3}
          sx={{
            position: 'absolute',
            top: `${hoverElement.position.top}px`,
            left: `${hoverElement.position.left}px`,
            zIndex: 1000,
            display: 'flex',
            padding: '2px',
            backgroundColor: '#f5f5f5',
            transform: `translate(90%, 25%)`,
          }}
          onMouseLeave={handleTooltipLeave}
        >
          <Tooltip title="Edit Link">
            <IconButton
              size="small"
              onClick={() =>
                handleEditLink(hoverElement.element as HTMLElement)
              }
            >
              <DatasetLinkedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove Link">
            <IconButton
              size="small"
              onClick={() =>
                handleRemoveLink(hoverElement.element as HTMLElement)
              }
            >
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}
      {selectedRangeElement.element && selectedRangeElement.position && (
        <Paper
          ref={tooltipRef}
          elevation={3}
          sx={{
            position: 'absolute',
            top: `${selectedRangeElement.position.top}px`,
            left: `${selectedRangeElement.position.left}px`,
            zIndex: 1000,
            display: 'flex',
            padding: '2px',
            backgroundColor: '#f5f5f5',
            transform: `translate(90%, 25%)`,
          }}
          onMouseLeave={handleTooltipSelectionRangeLeave}
        >
          <Tooltip title="Edit Link">
            <IconButton size="small" onClick={() => handleIconClick()}>
              <DatasetLinkedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      <DocumentDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onDocumentSelect={handleDocumentSelect}
        selectedDocumentIds={selectedDocumentIds}
        yearOptions={yearOptions}
        moduleOptions={moduleOptions}
        topicOptions={topicOptions}
        documents={documents}
      />
    </Box>
  )
}
